<template>
    <div class="bg-white">
        <!-- Mobile filter dialog -->
        <TransitionRoot as="template" :show="mobileFiltersOpen">
            <Dialog class="relative z-40 lg:hidden" @close="mobileFiltersOpen = false">
                <TransitionChild as="template" enter="transition-opacity ease-linear duration-300"
                    enter-from="opacity-0" enter-to="opacity-100"
                    leave="transition-opacity ease-linear duration-300" leave-from="opacity-100"
                    leave-to="opacity-0">
                    <div class="fixed inset-0 bg-black/50" />
                </TransitionChild>

                <div class="fixed inset-0 z-40 flex">
                    <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                        enter-from="translate-x-full" enter-to="translate-x-0"
                        leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
                        leave-to="translate-x-full">
                        <DialogPanel
                            class="relative ml-auto flex size-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12">
                            <div class="flex items-center justify-between px-6 pb-4 border-b border-[#E5E5E5]">
                                <h2 class="text-sm font-semibold uppercase tracking-widest text-[#17364a]">Filters</h2>
                                <button type="button"
                                    class="p-2 text-gray-400 hover:text-[#17364a] transition-colors"
                                    @click="mobileFiltersOpen = false">
                                    <span class="sr-only">Close menu</span>
                                    <XMarkIcon class="size-5" aria-hidden="true" />
                                </button>
                            </div>
                            <form class="mt-2">
                                <ul role="list" class="px-4 py-2 text-sm">
                                    <li v-for="category in categories" :key="category.name">
                                        <a :href="category.href"
                                            class="block py-3 border-b border-[#F5F5F5] text-gray-700 hover:text-[#A07830] transition-colors">
                                            {{ category.name }}
                                        </a>
                                    </li>
                                </ul>
                            </form>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>

        <main class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="flex items-baseline justify-between border-b border-[#E5E5E5] pt-8 pb-5">
                <h1 class="text-3xl font-bold tracking-tight text-[#17364a]">Courses</h1>

                <div class="flex items-center gap-3">
                    <Menu as="div" class="relative inline-block text-left">
                        <MenuButton
                            class="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-[#17364a] transition-colors">
                            Sort
                            <ChevronDownIcon class="h-4 w-4" aria-hidden="true" />
                        </MenuButton>
                        <transition enter-active-class="transition ease-out duration-100"
                            enter-from-class="opacity-0 translate-y-1"
                            enter-to-class="opacity-100 translate-y-0"
                            leave-active-class="transition ease-in duration-75"
                            leave-from-class="opacity-100 translate-y-0"
                            leave-to-class="opacity-0 translate-y-1">
                            <MenuItems
                                class="absolute right-0 z-10 mt-1 w-40 bg-white border border-[#E5E5E5] shadow-lg focus:outline-none">
                                <div class="py-1">
                                    <MenuItem v-for="option in sortOptions" :key="option.name" v-slot="{ active }">
                                    <a :href="option.href"
                                        :class="[
                                            option.current ? 'font-semibold text-[#17364a]' : 'text-gray-600',
                                            active ? 'bg-[#F5F5F5]' : '',
                                            'block px-4 py-2.5 text-sm border-b border-[#F5F5F5] last:border-0'
                                        ]">
                                        {{ option.name }}
                                    </a>
                                    </MenuItem>
                                </div>
                            </MenuItems>
                        </transition>
                    </Menu>

                    <button type="button" class="p-1.5 text-gray-400 hover:text-[#17364a] transition-colors">
                        <span class="sr-only">View grid</span>
                        <Squares2X2Icon class="size-5" aria-hidden="true" />
                    </button>
                    <button type="button" class="p-1.5 text-gray-400 hover:text-[#17364a] transition-colors lg:hidden"
                        @click="mobileFiltersOpen = true">
                        <span class="sr-only">Filters</span>
                        <FunnelIcon class="size-5" aria-hidden="true" />
                    </button>
                </div>
            </div>

            <section aria-labelledby="products-heading" class="pt-6 pb-24">
                <h2 id="products-heading" class="sr-only">Products</h2>
                <div class="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-4">
                    <!-- Sidebar filters -->
                    <form class="hidden lg:block">
                        <h3 class="text-xs font-semibold uppercase tracking-widest text-[#A07830] mb-4">Categories</h3>
                        <ul role="list" class="text-sm">
                            <li v-for="category in categories" :key="category.name">
                                <a :href="category.href"
                                    class="block py-2.5 border-b border-[#F5F5F5] text-gray-600 hover:text-[#A07830] transition-colors">
                                    {{ category.name }}
                                </a>
                            </li>
                        </ul>
                    </form>

                    <!-- Product grid -->
                    <div class="lg:col-span-3">
                        <slot name="content"></slot>
                    </div>
                </div>
            </section>
        </main>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Dialog, TransitionRoot, TransitionChild } from '@headlessui/vue';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import { ChevronDownIcon, FunnelIcon, Squares2X2Icon, XMarkIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
    products: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] },
    sortOptions: { type: Array, default: () => [] }
});

const mobileFiltersOpen = ref(false);
</script>
